
import React from "react";
import Searchkit from "searchkit";
import Client from "@searchkit/instantsearch-client";
import {
  InstantSearch,
  Hits,
  Pagination,
  HitsPerPage,
  useInstantSearch,
  useHits
} from "react-instantsearch";
import DebouncedSearchBox from "./DebouncedSearchBox";
import { history } from "instantsearch.js/es/lib/routers";
import { singleIndex } from "instantsearch.js/es/lib/stateMappings";

import "instantsearch.css/themes/satellite.css";
import "./styles.css";
import { searchkitConfig, indexName } from "./config/searchkit-settings";
import { getCurrentSort } from "./sortStore";

import CustomStats from "./CustomStats";
import Hit from "./ResultView";
import PhotoGrid from "./PhotoGrid";
import SkeletonHit from "./SkeletonHit";
import SidebarFacets from "./SidebarFacets";
import CustomCurrentRefinements from "./CustomCurrentRefinements";
import FuzzyNotice from "./FuzzyNotice";
import { setFuzzyFallback } from "./fuzzyStore";

const sk = new Searchkit(searchkitConfig);

// --- Utilitaires de réécriture de requête (phrases exactes + repli fuzzy) ---

// Trouve le premier multi_match dans l'arbre de requête (requête textuelle générée par Searchkit)
function findFirstMultiMatch(node) {
  if (!node || typeof node !== "object") return null;
  if (Array.isArray(node)) {
    for (const item of node) {
      const found = findFirstMultiMatch(item);
      if (found) return found;
    }
    return null;
  }
  if (node.multi_match && typeof node.multi_match === "object") return node.multi_match;
  for (const value of Object.values(node)) {
    const found = findFirstMultiMatch(value);
    if (found) return found;
  }
  return null;
}

// Applique une transformation à chaque multi_match (parcours récursif)
function mapMultiMatches(node, fn) {
  if (Array.isArray(node)) return node.map((n) => mapMultiMatches(n, fn));
  if (!node || typeof node !== "object") return node;
  const out = {};
  for (const [key, value] of Object.entries(node)) {
    if (key === "multi_match" && value && typeof value === "object") {
      out[key] = fn(value);
    } else {
      out[key] = mapMultiMatches(value, fn);
    }
  }
  return out;
}

// Bonus « multi-champs » (tie_breaker de best_fields / dis_max) :
// quand un mot est trouvé dans PLUSIEURS champs, une petite part des autres champs s'ajoute
// au score du meilleur champ. 0 = seul le meilleur champ compte ; 1 = somme de tous les champs.
// 0,1 = la répétition compte, sans abuser.
const TIE_BREAKER = 0.1;

// true le temps du second passage « relâché » (fuzzy réactivé si 0 résultat)
let forceRelaxed = false;

const beforeSearch = async (searchRequests) => {
  return searchRequests.map((sr) => {
    const body = { ...sr.body };

        // Appliquer le tri sélectionné par l'utilisateur (TriPar maison)
        const currentSort = getCurrentSort(); // null = Pertinence, [{corrigeDate:"desc"}] sinon
        if (currentSort) {
          body.sort = currentSort;
        }

        // Transformer les filtres NOT: (fonds) en must_not natif
        if (body.query && body.query.bool && body.query.bool.filter) {
          const newFilter = [];
          const mustNot = [];
          for (const clause of body.query.bool.filter) {
            if (clause.term) {
              const field = Object.keys(clause.term)[0];
              const val = clause.term[field];
              if (typeof val === "string" && val.startsWith("NOT:")) {
                mustNot.push({ term: { [field]: val.slice(4) } });
              } else {
                newFilter.push(clause);
              }
            } else if (clause.bool && clause.bool.should) {
              // Disjunctive filter (bool.should) — vérifier les NOT: à l'intérieur
              const newShould = [];
              for (const sub of clause.bool.should) {
                if (sub.term) {
                  const field = Object.keys(sub.term)[0];
                  const val = sub.term[field];
                  if (typeof val === "string" && val.startsWith("NOT:")) {
                    mustNot.push({ term: { [field]: val.slice(4) } });
                  } else {
                    newShould.push(sub);
                  }
                } else {
                  newShould.push(sub);
                }
              }
              if (newShould.length > 0) {
                newFilter.push({ bool: { should: newShould } });
              }
            } else {
              newFilter.push(clause);
            }
          }
          if (mustNot.length > 0) {
            body.query = {
              bool: {
                ...body.query.bool,
                filter: newFilter,
                must_not: [...(body.query.bool.must_not || []), ...mustNot],
              },
            };
          }
        }

        // --- Recherche en phrase exacte "entre guillemets" ---
        const firstMM = findFirstMultiMatch(body.query);
        const firstQuery = firstMM && typeof firstMM.query === "string" ? firstMM.query : "";
        if (firstMM && /"[^"]+"/.test(firstQuery)) {
          const fields = firstMM.fields || [];
          const phrases = [];
          const re = /"([^"]+)"/g;
          let m;
          while ((m = re.exec(firstQuery)) !== null) phrases.push(m[1]);
          const rest = firstQuery.replace(/"[^"]+"/g, " ").replace(/\s+/g, " ").trim();

          const must = phrases.map((p) => ({ multi_match: { query: p, type: "phrase", fields } }));
          if (rest) must.push({ multi_match: { query: rest, fields } });
          const phraseQuery = { bool: { must } };

          if (body.query && body.query.bool && body.query.bool.must) {
            body.query = { ...body.query, bool: { ...body.query.bool, must: phraseQuery } };
          } else {
            body.query = phraseQuery;
          }
        }

        // --- Passe stricte : exiger tous les termes (réduit le bruit de l'OR par défaut de Searchkit) ---
        // En repli fuzzy on relâche (opérateur OR + fuzziness) ; en passe normale on resserre.
        if (forceRelaxed) {
          body.query = mapMultiMatches(body.query, (mm) => {
            const t = mm.type || "best_fields";
            if (t === "phrase" || t === "bool_prefix") return mm; // fuzziness non autorisée
            return { ...mm, fuzziness: "AUTO", operator: "or", tie_breaker: TIE_BREAKER };
          });
        } else {
          body.query = mapMultiMatches(body.query, (mm) => {
            const t = mm.type || "best_fields";
            if (t === "phrase" || t === "bool_prefix") return mm; // operator non supporté
            return { ...mm, operator: "and", tie_breaker: TIE_BREAKER };
          });
        }

        // Tri par défaut : quand pas de tri explicite et pas de recherche textuelle
        // → date de modification décroissante (donne une impression de moteur "vivant")
        const hasQuery = body.query && !JSON.stringify(body.query).includes('"match_all"');
        const hasExplicitSort = body.sort && body.sort.length > 0;
        if (!hasQuery && !hasExplicitSort) {
          body.sort = [{ corrigeDate: "desc" }];
        }

        return { ...sr, body: { ...body, track_total_hits: true } };
  });
};

const baseSearchClient = Client(sk, { hooks: { beforeSearch } });

// Client enveloppé : si le 1er passage ne renvoie aucun résultat, on relance en mode « relâché »
// (fuzzy réactivé) et on signale le repli au frontend via fuzzyStore.
const searchClient = {
  search: async (requests) => {
    setFuzzyFallback(false);
    forceRelaxed = false;

    let res = await baseSearchClient.search(requests);
    const first = res && res.results && res.results[0];
    const query = first && first.query ? first.query : "";

    if (first && first.nbHits === 0 && query.trim().length > 0) {
      forceRelaxed = true;
      try {
        await baseSearchClient.clearCache();
      } catch (e) {
        /* ignoré */
      }
      res = await baseSearchClient.search(requests);
      forceRelaxed = false;
      // On purge le cache pour ne pas resservir le résultat « relâché » à la prochaine requête identique
      try {
        await baseSearchClient.clearCache();
      } catch (e) {
        /* ignoré */
      }
      if (res && res.results && res.results[0]) {
        res.results[0].fuzzyFallback = true;
        setFuzzyFallback(true);
      }
    }

    return res;
  },
  searchForFacetValues: (requests) => baseSearchClient.searchForFacetValues(requests),
  clearCache: () => baseSearchClient.clearCache(),
};

function GridToggle({ gridMode, setGridMode }) {
  return (
    <div style={{ textAlign: "right", marginBottom: "12px" }}>
      <button
        type="button"
        onClick={() => setGridMode(!gridMode)}
        style={{
          border: "1px solid #ccc",
          borderRadius: "4px",
          padding: "4px 12px",
          fontSize: "0.8rem",
          cursor: "pointer",
        background: gridMode ? "#fff" : "#3a56e4",
        color: gridMode ? "#333" : "#fff",
        }}
        title={gridMode ? "Vue liste" : "Vue galerie"}
      >
        {gridMode ? "☰ Liste" : "⊞ Galerie"}
      </button>
    </div>
  );
}

function DynamicHits({ gridMode, photoOnly, setGridMode }) {
  return (
    <>
      {photoOnly && <GridToggle gridMode={gridMode} setGridMode={setGridMode} />}
      {gridMode ? (
        <div className="photo-grid-wrapper">
          <PhotoGrid />
        </div>
      ) : (
        <Hits hitComponent={Hit} />
      )}
    </>
  );
}

function SearchContent() {
  const { status, results, indexUiState } = useInstantSearch();
  const { hits } = useHits();
  const loading = status === "loading" || status === "stalled";
  const [gridMode, setGridMode] = React.useState(false);

  const docTypeValues = indexUiState?.refinementList?.docType || [];
  const photoOnly = docTypeValues.length === 1 && docTypeValues[0] === "k";

  // Reset grid mode si on quitte le mode photo seul
  if (!photoOnly && gridMode) setGridMode(false);

  // Nombre de skeletons à afficher : le nb de résultats actuels ou 10 par défaut
  const skeletonCount = hits.length > 0 ? hits.length : (results?.hitsPerPage || 10);

  return (
    <>
      <div className="sui-layout-header">
        <div className="sui-layout-header__inner">
          <DebouncedSearchBox 
            placeholder="Rechercher..." 
            className="sui-search-box"
          />
        </div>
      </div>

      <div className="sui-layout-body">
        <SidebarFacets />

        <div className="sui-layout-main">
          <div className="sui-layout-main-header">
            <div className="sui-layout-main-header__top">
              <CustomStats />
              <HitsPerPage
                items={[
                  { label: "10 résultats", value: 10, default: true },
                  { label: "20 résultats", value: 20 },
                  { label: "50 résultats", value: 50 }
                ]}
              />
            </div>
            <div className="sui-layout-main-header__filters">
              <CustomCurrentRefinements />
            </div>
          </div>

          {/* Bandeau « résultats approchants » si la recherche a été élargie (0 résultat exact) */}
          <FuzzyNotice />

          {/* Stale-while-revalidate : squelette uniquement si pas de résultats précédents */}
          {loading && hits.length === 0 ? (
            <div style={{ opacity: 0.7 }}>
              {Array.from({ length: skeletonCount }, (_, i) => (
                <SkeletonHit key={i} withImage={i % 3 === 0} />
              ))}
            </div>
          ) : (
            <DynamicHits gridMode={gridMode} photoOnly={photoOnly} setGridMode={setGridMode} />
          )}
          <div className="sui-layout-main-footer">
            <Pagination />
          </div>
        </div>
      </div>
    </>
  );
}

export default function App() {
  const routingProps = {
    router: history(),
    stateMapping: singleIndex(indexName),
  };

  return (
    <InstantSearch searchClient={searchClient} indexName={indexName} routing={routingProps}>
      <div className="App">
        <div id="banniere-wrap">
          <a href="https://archives.memovs.ch/search/" className="logo"> </a>
        </div>
        
        <div className="sui-layout-container">
          <div className="sui-layout">
            <SearchContent />
          </div>
        </div>
      </div>
    </InstantSearch>
  );
}

